import React, { useEffect, useState } from 'react';
import { GraphMember, useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { X, Link2, Loader2, Mail, ShieldCheck, AlertTriangle, Bug, CheckCircle2, Wrench, Send } from 'lucide-react';

interface Props {
  open: boolean;
  member: GraphMember;
  onClose: () => void;
}

/**
 * Owner / admin-only "Connect Existing User to This Tree" modal.
 *
 * v6 — STALE/MISSING ROW AUTO-RECREATE + INVITE-LINK FALLBACK:
 *   The previous v5 client-side resolver caught most cases of a stale
 *   prop id, but a real failure mode remained: when the visible tree
 *   card refers to a `family_graph_members` row that has been DELETED
 *   from the database (or was never persisted because it was a synthetic
 *   anchor / orphaned snapshot card), no client-side strategy can
 *   resurrect it. The edge function would then return `member_not_found`
 *   / 404 even though the user was looking at a real card.
 *
 *   Fix: we now ALWAYS send a `repair` payload alongside the request.
 *   The payload describes the visible card (owner_user_id + name +
 *   relationship + side + photo + birthYear + relationshipOverride) so
 *   the server can:
 *     1. Re-fetch by id (existing).
 *     2. If missing, re-resolve by exact name + owner (server-side).
 *     3. If still missing, INSERT a fresh placeholder row with the
 *        provided fields (service role, permission-checked), then bind.
 *
 *   Tree structure, parent_ids, relationships, photos, memories,
 *   payments and membership gates are NEVER touched. Only the four
 *   claim columns (claimed_by_user_id, profile_state, claimed_at,
 *   updated_at) are written on a successful bind. The recreate path
 *   inserts a NEW row (placeholder) for the missing card; it does not
 *   modify or delete any existing data.
 *
 *   We also add a "Use invite link instead" action — a no-email
 *   alternative that copies a fresh invite URL for this tree to the
 *   clipboard so the operator can text/share it directly.
 */

interface DebugResult {
  functionCalled: boolean;
  functionName: string;
  functionVersion: string | null;
  selectedMemberIdSent: string | null;
  memberIdSource: 'prop' | 'context' | 'name-lookup' | 'unresolved' | null;
  /** Did we send a repair payload alongside the request? */
  repairSent: boolean;
  /** Server-side outcome: did the row need auto-recreate? */
  autoRecreated: boolean | null;
  httpStatus: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  userFound: boolean | null;
  rowUpdated: boolean | null;
  accessTokenPresent: boolean;
  callerIdResolved: boolean | null;
  jwtSub: string | null;
  callerProfileFound: boolean | null;
  permissionGranted: boolean | null;
  permissionReason: string | null;
  targetRowFound: boolean | null;
  targetRowName: string | null;
  targetOwnerUserId: string | null;
  ownerMatch: boolean | null;
  targetEmailLookup: string | null;
  emailPrecheck: 'found' | 'not_found' | 'rls_blocked' | null;
  rawResponse: any;
  callerDiag?: any;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normName = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();


const ConnectUserModal: React.FC<Props> = ({ open, member, onClose }) => {
  const { refresh, members: ctxMembers, effectiveOwnerUserId } = useFamilyGraph();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [debug, setDebug] = useState<DebugResult | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setSubmitting(false);
      setDebug(null);
    }
  }, [open, member?.id]);

  if (!open) return null;

  const alreadyClaimed = member.profileState === 'claimed' || !!member.claimedByUserId;

  const friendlyError = (code: string | undefined, fallback: string): string => {
    switch (code) {
      case 'no_email':
        return 'Please enter an email address.';
      case 'invalid_email':
        return 'That doesn\'t look like a valid email address.';
      case 'no_member_id':
      case 'invalid_member_id':
      case 'member_not_found':
      case 'no_row_for_member_id':
        return 'Selected profile row could not be found or recreated. Refresh the page and try again, or use the invite link instead.';
      case 'unauthenticated':
        return 'Your session expired. Please sign out and sign back in, then retry.';
      case 'forbidden':
        return 'Connection failed — only the family owner or an admin can do this.';
      case 'user_not_found':
      case 'email_not_registered':
        return 'No account found for this email — have them log in first or use the invite link.';
      case 'already_claimed':
        return 'This profile is already claimed.';
      case 'conflict_other_claim':
        return 'That user already has another claimed profile in this tree. Unclaim the other profile first, then try again.';
      case 'race_lost':
        return 'This profile was just claimed by someone else. Please refresh and try again.';
      case 'write_failed':
      case 'server_error':
        return 'Connection failed — please try again in a moment.';
      case 'no_session_client':
        return 'You\'re not signed in on this device. Please sign in again, then retry.';
      case 'function_unreachable':
        return 'Could not reach the connect-user service. Check your internet connection and try again.';
      default:
        return fallback;
    }
  };

  /**
   * Try every available strategy to find a real `family_graph_members.id`
   * for the profile the user opened the modal on. v5 logic — preserved
   * because it still helps the *common* case where the prop id was just
   * stale-against-context. The server-side recreate path (v6) handles the
   * residual case where the row no longer exists at all.
   */
  const resolveMemberId = async (): Promise<
    | { id: string; source: 'prop' | 'context' | 'name-lookup'; name: string | null; ownerUserId: string | null }
    | null
  > => {
    const propId = (member?.id ?? '').trim();
    const propIsUuid = !!propId && UUID_RE.test(propId) && propId !== 'self';
    const propName = (member?.name ?? '').trim();
    const ownerHint = effectiveOwnerUserId || member?.ownerUserId || null;

    if (propIsUuid) {
      const hit = ctxMembers.find((m) => m.id === propId);
      if (hit) {
        return {
          id: hit.id,
          source: 'context',
          name: hit.name ?? propName ?? null,
          ownerUserId: hit.ownerUserId ?? ownerHint,
        };
      }
      try {
        const { data, error } = await supabase
          .from('family_graph_members')
          .select('id, owner_user_id, name')
          .eq('id', propId)
          .is('deleted_at', null)
          .maybeSingle();
        if (!error && data) {
          return {
            id: data.id,
            source: 'prop',
            name: (data as any).name ?? propName ?? null,
            ownerUserId: (data as any).owner_user_id ?? ownerHint,
          };
        }
      } catch { /* fall through */ }
    }

    if (propName && ownerHint) {
      const ctxByName = ctxMembers.filter(
        (m) =>
          m.ownerUserId === ownerHint && normName(m.name) === normName(propName),
      );
      if (ctxByName.length === 1) {
        const m = ctxByName[0];
        return {
          id: m.id,
          source: 'name-lookup',
          name: m.name,
          ownerUserId: m.ownerUserId,
        };
      }
      try {
        const { data } = await supabase
          .from('family_graph_members')
          .select('id, owner_user_id, name')
          .eq('owner_user_id', ownerHint)
          .is('deleted_at', null)
          .ilike('name', propName)
          .limit(5);
        const rows = data ?? [];
        const exact = rows.filter(
          (r: any) => normName(r.name) === normName(propName),
        );
        if (exact.length === 1) {
          return {
            id: (exact[0] as any).id,
            source: 'name-lookup',
            name: (exact[0] as any).name ?? propName,
            ownerUserId: (exact[0] as any).owner_user_id ?? ownerHint,
          };
        }
      } catch { /* swallow */ }
    }

    return null;
  };

  const precheckEmailExists = async (
    emailLower: string,
  ): Promise<'found' | 'not_found' | 'rls_blocked'> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .ilike('email', emailLower)
        .limit(1);
      if (error) return 'rls_blocked';
      if (Array.isArray(data) && data.length > 0) return 'found';
      return 'not_found';
    } catch {
      return 'rls_blocked';
    }
  };

  /**
   * Build the `repair` payload sent to the server. Always describes the
   * visible card so the server can recreate the row if its id is dead.
   * Pulls the freshest copy from context (if it exists) before falling
   * back to the prop. We never invent relationships/sides not on the
   * card — only what's already shown.
   */
  const buildRepairPayload = () => {
    const fromCtx = ctxMembers.find((m) => m.id === member?.id);
    const live = fromCtx ?? member;
    const ownerUserId = live?.ownerUserId || effectiveOwnerUserId || null;
    if (!ownerUserId || !live?.name) return null;
    return {
      ownerUserId,
      name: String(live.name).trim(),
      relationship: live.relationship || 'unknown',
      side: live.side || 'self',
      photoUrl: live.photoUrl ?? null,
      birthYear: live.birthYear ?? null,
      relationshipOverride: live.relationshipOverride ?? null,
    };
  };

  /**
   * Copy a no-email fallback invite URL for this tree to the clipboard.
   * Uses /invite/{owner_user_id} (without a token — InviteLanding
   * accepts legacy non-token paths). Owners typically have a richer
   * tokenized link from InviteModal; this is purely an "I just need
   * something to text right now" escape hatch.
   */
  const handleCopyInviteLink = async () => {
    const ownerId = effectiveOwnerUserId || member?.ownerUserId;
    if (!ownerId) {
      toast.error('Could not build invite link — owner not resolved.');
      return;
    }
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://www.kinscircleapp.com';
    const url = `${base}/invite/${ownerId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied.', {
        description: 'Send it to them by text. They can join with no email setup.',
      });
    } catch {
      // Fallback for older browsers without clipboard API
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Invite link copied.');
      } catch {
        toast.error('Could not copy. Long-press the link below to copy manually.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter an email address.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setDebug(null);

    const result: DebugResult = {
      functionCalled: false,
      functionName: 'connect-user-to-profile',
      functionVersion: null,
      selectedMemberIdSent: null,
      memberIdSource: null,
      repairSent: false,
      autoRecreated: null,
      httpStatus: null,
      errorCode: null,
      errorMessage: null,
      userFound: null,
      rowUpdated: null,
      accessTokenPresent: false,
      callerIdResolved: null,
      jwtSub: null,
      callerProfileFound: null,
      permissionGranted: null,
      permissionReason: null,
      targetRowFound: null,
      targetRowName: null,
      targetOwnerUserId: null,
      ownerMatch: null,
      targetEmailLookup: null,
      emailPrecheck: null,
      rawResponse: null,
    };

    // ----- STEP 1: client-side id resolution (best effort) ---------------
    // We always attempt this so we send the freshest known id, but a
    // failure here NO LONGER aborts the flow — the server-side recreate
    // path (v10) handles the case where the row genuinely doesn't exist.
    const resolved = await resolveMemberId();
    let memberIdToSend: string;
    if (resolved) {
      memberIdToSend = resolved.id;
      result.selectedMemberIdSent = resolved.id;
      result.memberIdSource = resolved.source;
      result.targetRowFound = true;
      result.targetRowName = resolved.name;
      result.targetOwnerUserId = resolved.ownerUserId;
    } else {
      // Send the prop id so the server can attempt a re-resolve + recreate
      // using the repair payload. The id has to be a non-empty string for
      // the server's input validator to accept the call; we use the prop
      // id (or a synthetic placeholder) so it can be looked up first.
      memberIdToSend = (member?.id ?? '').trim() || '00000000-0000-0000-0000-000000000000';
      result.selectedMemberIdSent = memberIdToSend;
      result.memberIdSource = 'unresolved';
      result.targetRowFound = false;
      result.targetRowName = (member?.name ?? '').trim() || null;
      result.targetOwnerUserId = effectiveOwnerUserId || member?.ownerUserId || null;
      console.warn('[ConnectUserModal] could not resolve member id locally — relying on server-side recreate', {
        propId: member?.id,
        propName: member?.name,
        ownerHint: effectiveOwnerUserId || member?.ownerUserId,
      });
    }

    // Build the repair payload describing the visible card. Always send
    // it — even when client resolution succeeded — so the server can
    // recreate if a race deletes the row between our read and the
    // server's read.
    const repair = buildRepairPayload();
    result.repairSent = !!repair;

    console.debug('[ConnectUserModal] submitting', {
      memberIdToSend,
      memberIdSource: result.memberIdSource,
      repair,
    });

    try {
      // ----- STEP 2: best-effort email pre-check -----------------------
      const emailCheck = await precheckEmailExists(trimmed);
      result.emailPrecheck = emailCheck;

      // ----- STEP 3: resolve the user's access token -------------------
      let accessToken: string | undefined;
      try {
        const refreshed = await supabase.auth.refreshSession();
        accessToken = refreshed?.data?.session?.access_token;
      } catch { /* non-fatal */ }
      if (!accessToken) {
        const sessionRes = await supabase.auth.getSession();
        accessToken = sessionRes.data.session?.access_token;
      }
      result.accessTokenPresent = !!accessToken;
      try {
        const userRes = await supabase.auth.getUser();
        result.jwtSub = userRes.data.user?.id ?? null;
        if (result.jwtSub && result.targetOwnerUserId) {
          result.ownerMatch = result.jwtSub === result.targetOwnerUserId;
        }
      } catch { /* ignore */ }

      if (!accessToken) {
        result.errorCode = 'no_session_client';
        result.errorMessage = 'Please log out and sign back in.';
        setDebug(result);
        toast.error('Please log out and sign back in.');
        return;
      }

      // ----- STEP 4: invoke the edge function --------------------------
      let invokeResp: any = null;
      let invokeErr: any = null;
      try {
        invokeResp = await supabase.functions.invoke('connect-user-to-profile', {
          body: {
            memberId: memberIdToSend,
            email: trimmed,
            debug: true,
            // Always include — server will only use it if it has to.
            repair,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch (e: any) {
        invokeErr = e;
      }

      result.functionCalled = true;
      const data = invokeResp?.data;
      const error = invokeResp?.error || invokeErr;
      result.rawResponse = data ?? null;

      const ingestDiag = (envelope: any) => {
        if (!envelope) return;
        if (envelope.functionVersion) result.functionVersion = envelope.functionVersion;
        if (envelope.autoRecreated !== undefined) result.autoRecreated = !!envelope.autoRecreated;
        if (envelope.callerDiag) {
          const d = envelope.callerDiag;
          result.callerDiag = d;
          if (d.functionVersion && !result.functionVersion) {
            result.functionVersion = d.functionVersion;
          }
          if (d.callerIdResolved !== undefined) result.callerIdResolved = !!d.callerIdResolved;
          if (d.jwtSub) result.jwtSub = d.jwtSub;
          if (d.callerProfileFound !== undefined) result.callerProfileFound = d.callerProfileFound;
          if (d.permissionGranted !== undefined) result.permissionGranted = d.permissionGranted;
          if (d.permissionReason !== undefined) result.permissionReason = d.permissionReason;
          if (d.targetRowFound !== undefined) result.targetRowFound = !!d.targetRowFound;
          if (d.targetOwnerUserId !== undefined) result.targetOwnerUserId = d.targetOwnerUserId;
          if (d.targetMemberId) {
            // server may have returned a different id (recreated row).
            result.selectedMemberIdSent = d.targetMemberId;
          }
          if (d.targetMemberName) result.targetRowName = d.targetMemberName;
          if (d.ownerMatch !== undefined) result.ownerMatch = !!d.ownerMatch;
          if (d.targetEmailLookupVia) {
            const found = d.targetEmailLookupFound ? 'found' : 'not found';
            result.targetEmailLookup = `${d.targetEmailLookupVia} (${found})`;
          }
          if (d.rowUpdated !== undefined) result.rowUpdated = !!d.rowUpdated;
          if (d.repairOk !== undefined) result.autoRecreated = !!d.repairOk;
          if (result.ownerMatch === null && result.jwtSub && result.targetOwnerUserId) {
            result.ownerMatch = result.jwtSub === result.targetOwnerUserId;
          }
        }
      };

      if (error) {
        let serverMsg: string | null = null;
        let serverCode: string | undefined;
        let parsed: any = null;
        let httpStatus: number | null = null;
        try {
          const ctx: any = (error as any).context;
          if (ctx) {
            if (typeof ctx.status === 'number') httpStatus = ctx.status;
            if (typeof ctx.json === 'function') {
              parsed = await ctx.json();
              serverMsg = parsed?.error || null;
              serverCode = parsed?.code;
            }
          }
        } catch { /* ignore */ }

        result.httpStatus = httpStatus;
        result.errorCode = serverCode || 'function_unreachable';
        result.errorMessage = serverMsg || error.message || 'Unknown error';
        ingestDiag(parsed);
        if (parsed?.userFound !== undefined) result.userFound = parsed.userFound;
        if (parsed?.rowUpdated !== undefined) result.rowUpdated = parsed.rowUpdated;
        if (parsed) result.rawResponse = parsed;

        if (
          serverCode === 'member_not_found' ||
          serverCode === 'no_row_for_member_id'
        ) {
          try { await refresh(); } catch { /* non-fatal */ }
        }

        console.warn('[ConnectUserModal] edge fn returned error', {
          httpStatus,
          serverCode,
          serverMsg,
          rawMessage: error.message,
          callerDiag: parsed?.callerDiag,
        });
        setDebug(result);
        toast.error(friendlyError(serverCode, serverMsg || error.message || 'Could not connect user.'));
        return;
      }

      result.httpStatus = 200;

      if (data?.error) {
        result.errorCode = data.code || 'soft_error';
        result.errorMessage = data.error;
        ingestDiag(data);
        setDebug(result);
        toast.error(friendlyError(data.code, data.error));
        return;
      }

      ingestDiag(data);
      result.userFound = data?.userFound ?? true;
      result.rowUpdated = data?.rowUpdated ?? !data?.alreadyClaimedBySameUser;
      if (data?.autoRecreated !== undefined) result.autoRecreated = !!data.autoRecreated;
      if (!data?.callerDiag) {
        if (result.callerIdResolved === null) result.callerIdResolved = true;
        if (result.callerProfileFound === null) result.callerProfileFound = true;
        if (result.permissionGranted === null) result.permissionGranted = true;
        if (result.targetRowFound === null) result.targetRowFound = true;
      }
      setDebug(result);

      if (data?.alreadyClaimedBySameUser) {
        toast.success('That account was already connected to this profile.', {
          description: 'No changes were needed.',
        });
      } else if (data?.autoRecreated) {
        toast.success('Profile repaired and account connected.', {
          description: 'The missing profile row was rebuilt and bound to that account.',
        });
      } else {
        toast.success('Account successfully connected.', {
          description: data?.autoCreatedProfile
            ? 'Their profile was created and linked to this tree.'
            : 'They\'ll see this shared tree the next time they log in.',
        });
      }

      try {
        await refresh();
      } catch { /* non-fatal */ }
      try {
        window.dispatchEvent(new CustomEvent('kc:family-graph-refresh'));
      } catch { /* non-fatal */ }

      setTimeout(() => {
        onClose();
      }, 1400);
    } catch (err: any) {
      console.error('[ConnectUserModal] unexpected exception', err);
      result.errorCode = 'client_exception';
      result.errorMessage = err?.message || String(err);
      setDebug(result);
      toast.error(err?.message || 'Could not connect user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };




  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 via-fuchsia-600 to-pink-500 text-white p-5 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <Link2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif font-bold text-lg">Connect existing user to this tree</div>
            <div className="text-xs opacity-90 mt-0.5">
              Bind {member.name || 'this profile'} to an account that already exists.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-[12px] text-indigo-900 leading-relaxed">
            <div className="font-bold mb-0.5 inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              Owner / admin repair tool
            </div>
            Use this only when someone already signed up but their account
            isn't connected to this shared family tree. After connecting, the
            next time they log in they'll see <strong>this</strong> tree, and
            this profile becomes their <em>You</em> card.
          </div>

          {alreadyClaimed && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 inline-flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                This profile is already claimed by another user. Connecting a
                different email will not work — release the existing claim
                first.
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="connect-email" className="text-xs font-bold uppercase tracking-wide text-slate-700">
              Account email
            </Label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                id="connect-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="daughter@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Must match the email they used to sign up. We'll find their
              account and connect it to <strong>{member.name || 'this profile'}</strong>.
            </div>
          </div>

          {/* No-email fallback */}
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-[11px] text-emerald-900 leading-relaxed">
            <div className="flex items-start gap-2">
              <Send className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold mb-0.5">Don't have their email?</div>
                <div className="mb-1.5">
                  Send them an invite link instead — they can join with no
                  email setup.
                </div>
                <button
                  type="button"
                  onClick={handleCopyInviteLink}
                  disabled={submitting}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 disabled:opacity-60"
                >
                  <Link2 className="w-3 h-3" />
                  Use invite link instead
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 leading-relaxed">
            <strong className="text-slate-800">Safe by design:</strong> only
            the connection metadata is changed. Photos, memories, parents,
            relationships, side, and tree placement on this profile stay
            exactly as they are. The user's own personal tree (if any) is
            left untouched — this shared tree will simply become their
            primary tree on next login.
          </div>

          {debug && (
            <div className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[11px] text-slate-700 space-y-1.5">
              {!debug.errorCode && debug.functionCalled && debug.rowUpdated !== false && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-[11px] text-emerald-900 inline-flex items-start gap-1.5 w-full">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    <strong>Success.</strong> {debug.targetRowName ? `${debug.targetRowName}'s` : 'This'} profile is now connected to that account
                    {debug.autoRecreated ? ' (the missing row was rebuilt automatically).' : '.'}
                  </span>
                </div>
              )}
              {debug.autoRecreated && !debug.errorCode && (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5 text-[11px] text-blue-900 inline-flex items-start gap-1.5 w-full">
                  <Wrench className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Auto-repair ran: the visible card had no underlying
                    family_graph_members row, so a fresh placeholder was
                    created and then bound to the account.
                  </span>
                </div>
              )}

              <div className="inline-flex items-center gap-1.5 font-bold text-slate-900 text-[12px]">
                <Bug className="w-3.5 h-3.5" />
                Debug result
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <div className="text-slate-500">Function called</div>
                <div className="font-mono">
                  {debug.functionCalled ? `yes — ${debug.functionName}` : 'no'}
                </div>

                <div className="text-slate-500">Function version</div>
                <div className="font-mono">{debug.functionVersion ?? '—'}</div>

                <div className="text-slate-500">Selected member id</div>
                <div className="font-mono break-all">{debug.selectedMemberIdSent ?? '—'}</div>

                <div className="text-slate-500">Member id source</div>
                <div className="font-mono">
                  {debug.memberIdSource === 'prop' && 'prop (DB-confirmed)'}
                  {debug.memberIdSource === 'context' && 'context (live tree)'}
                  {debug.memberIdSource === 'name-lookup' && 'name re-lookup (stale id repaired)'}
                  {debug.memberIdSource === 'unresolved' && 'unresolved (server-side recreate)'}
                  {debug.memberIdSource === null && '—'}
                </div>

                <div className="text-slate-500">Repair payload sent</div>
                <div className="font-mono">{debug.repairSent ? 'yes' : 'no'}</div>

                <div className="text-slate-500">Server auto-recreated row</div>
                <div className="font-mono">
                  {debug.autoRecreated === null ? '—' : debug.autoRecreated ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">Email pre-check</div>
                <div className="font-mono">
                  {debug.emailPrecheck === 'found' && 'profile row exists'}
                  {debug.emailPrecheck === 'not_found' && 'no profile row (server will confirm)'}
                  {debug.emailPrecheck === 'rls_blocked' && 'unknown (RLS-blocked)'}
                  {debug.emailPrecheck === null && '—'}
                </div>

                <div className="text-slate-500">Target row found</div>
                <div className="font-mono">
                  {debug.targetRowFound === null ? '—' : debug.targetRowFound ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">Target row name</div>
                <div className="font-mono break-words">{debug.targetRowName ?? '—'}</div>

                <div className="text-slate-500">Target owner_user_id</div>
                <div className="font-mono break-all">{debug.targetOwnerUserId ?? '—'}</div>

                <div className="text-slate-500">JWT sub</div>
                <div className="font-mono break-all">{debug.jwtSub ?? '—'}</div>

                <div className="text-slate-500">Owner match</div>
                <div className="font-mono">
                  {debug.ownerMatch === null ? '—' : debug.ownerMatch ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">Access token present</div>
                <div className="font-mono">{debug.accessTokenPresent ? 'yes' : 'no'}</div>

                <div className="text-slate-500">Caller user id resolved</div>
                <div className="font-mono">
                  {debug.callerIdResolved === null ? '—' : debug.callerIdResolved ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">HTTP status</div>
                <div className="font-mono">{debug.httpStatus ?? '—'}</div>

                <div className="text-slate-500">Error code</div>
                <div className="font-mono">{debug.errorCode ?? '—'}</div>

                <div className="text-slate-500">Error message</div>
                <div className="font-mono break-words">{debug.errorMessage ?? '—'}</div>

                <div className="text-slate-500">User found (by email)</div>
                <div className="font-mono">
                  {debug.userFound === null ? '—' : debug.userFound ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">Target email lookup</div>
                <div className="font-mono break-words">{debug.targetEmailLookup ?? '—'}</div>

                <div className="text-slate-500">Caller profile found</div>
                <div className="font-mono">
                  {debug.callerProfileFound === null ? '—' : debug.callerProfileFound ? 'yes' : 'no'}
                </div>

                <div className="text-slate-500">Permission granted</div>
                <div className="font-mono">
                  {debug.permissionGranted === null
                    ? '—'
                    : debug.permissionGranted
                      ? `yes${debug.permissionReason ? ` (${debug.permissionReason})` : ''}`
                      : `no${debug.permissionReason ? ` (${debug.permissionReason})` : ''}`}
                </div>

                <div className="text-slate-500">Row updated</div>
                <div className="font-mono">
                  {debug.rowUpdated === null ? '—' : debug.rowUpdated ? 'yes' : 'no'}
                </div>
              </div>

              {debug.callerDiag && (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-[10.5px] text-slate-500 hover:text-slate-700">
                    Auth diagnostic
                  </summary>
                  <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(debug.callerDiag, null, 2)}
                  </pre>
                </details>
              )}

              {(debug.errorCode === 'invalid_member_id' ||
                debug.errorCode === 'no_member_id' ||
                debug.errorCode === 'member_not_found' ||
                debug.errorCode === 'no_row_for_member_id') && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-900 mt-1.5 space-y-1">
                  <div>
                    <strong>Selected family member row not found, and could
                    not be auto-recreated.</strong> Refresh the page, reopen
                    the profile from the tree, then try again.
                  </div>
                  <div>
                    Or skip the email and use the invite link instead — tap
                    the green button above to copy a no-email join link.
                  </div>
                </div>
              )}
              {debug.errorCode === 'unauthenticated' && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-900 mt-1.5">
                  <strong>Most common cause:</strong> your sign-in token
                  wasn't accepted. Sign out, sign back in, then retry.
                </div>
              )}
              {debug.errorCode === 'forbidden' && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-900 mt-1.5">
                  <strong>Permission denied.</strong> Only the family owner
                  or an admin/staff account can run this repair tool.
                </div>
              )}
              {debug.errorCode === 'server_error' && (
                <div className="rounded-md bg-rose-50 border border-rose-200 px-2 py-1.5 text-[10.5px] text-rose-900 mt-1.5">
                  Server error while loading this profile. Refresh the page,
                  then try again. If it keeps happening, capture the auth
                  diagnostic above for support.
                </div>
              )}
              {debug.errorCode === 'no_session_client' && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-900 mt-1.5">
                  No session was found on this device. Please sign in again.
                </div>
              )}
              {debug.errorCode === 'function_unreachable' && (
                <div className="rounded-md bg-rose-50 border border-rose-200 px-2 py-1.5 text-[10.5px] text-rose-900 mt-1.5">
                  Could not reach <code>connect-user-to-profile</code>. The
                  function may not be deployed, or the network is blocked.
                </div>
              )}
              {(debug.errorCode === 'user_not_found' ||
                debug.errorCode === 'email_not_registered') && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-900 mt-1.5">
                  <strong>No account found for this email.</strong> Have them
                  sign up (or log in once) first, then run this tool again —
                  or send them the invite link from this tree, which lets
                  them join with no email setup needed.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {debug && !debug.errorCode ? 'Close' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={submitting || alreadyClaimed || !email.trim()}
              className="bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-pink-500 hover:opacity-95 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {submitting ? 'Connecting…' : 'Connect account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectUserModal;
